import { observer } from "mobx-react";
import * as React from "react";
import { FormattedNumber } from "react-intl";
import { Card, Skeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "randmarcomps";
import { Info } from "lucide-react";
import { InstantRebateInput } from "randmar-api-client";
import { InstantRebate as LegacyInstantRebate } from "../../stores/product/Product";
import CurrencyUtils from "../util/CurrencyUtils";
import { FormatDateFromString } from "../util/DateTimeUtil";

interface IProps {
  instantRebates?: (LegacyInstantRebate | InstantRebateInput)[];
}

const InstantRebatesGrid: React.FC<IProps> = ({ instantRebates }) => {

  const sortedRebates = React.useMemo(() => {
    if (!instantRebates) {
      return [];
    }
    const isLegacy = (rebate: LegacyInstantRebate | InstantRebateInput): rebate is LegacyInstantRebate => 'rebateAmount' in rebate;

    const normalized = instantRebates.map(rebate => {
      if (isLegacy(rebate)) {
        return {
          PromotionId: rebate.promotionId,
          StartDate: rebate.startDate,
          EndDate: rebate.endDate,
          Rebate: rebate.rebateAmount
        };
      }
      return rebate;
    });

    return normalized.sort((a, b) => (b.EndDate || "").localeCompare(a.EndDate || ""));
  }, [instantRebates]);


  if (!instantRebates) {
    return (
      <Card className="w-full">
        <div className="w-full overflow-auto rounded-[20px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Promotion Id</TableHead>
                <TableHead className="text-center">Start date</TableHead>
                <TableHead className="text-center">End date</TableHead>
                <TableHead className="text-right">Rebate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 3 }).map((_, index) => (
                <TableRow key={index} className="hover:bg-purple-50">
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell className="text-center"><Skeleton className="h-4 w-20 mx-auto" /></TableCell>
                  <TableCell className="text-center"><Skeleton className="h-4 w-20 mx-auto" /></TableCell>
                  <TableCell className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    );
  }

  if (sortedRebates.length === 0) {
    return (
      <Card className="w-full bg-white p-6">
        <div className="flex flex-col items-center justify-center gap-2 text-center text-slate-500">
          <Info className="h-10 w-10" />
          <p>There are no instant rebates for this product.</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="w-full">
        <div className="w-full overflow-auto rounded-[20px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Promotion Id</TableHead>
                <TableHead className="text-center">Start date</TableHead>
                <TableHead className="text-center">End date</TableHead>
                <TableHead className="text-right">Rebate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRebates.map((ir, index) => (
                  <TableRow className="hover:bg-purple-50" key={ir.PromotionId || index}>
                    <TableCell>{ir.PromotionId}</TableCell>
                    <TableCell className="text-center">{FormatDateFromString(ir.StartDate)}</TableCell>
                    <TableCell className="text-center">{FormatDateFromString(ir.EndDate)}</TableCell>
                    <TableCell className="text-right"><FormattedNumber value={ir.Rebate || 0}  {...CurrencyUtils.format} /></TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    );
}

export default observer(InstantRebatesGrid);
